/**
 * SEO keyword variants per calculator page.
 * Used by buildSeo() to inject <meta name="keywords"> + augment the description,
 * and by CalculatorLayout to render a visible "Also known as" block.
 *
 * Keys are the route path starting with "/".
 */

export interface SeoAlias {
  /** Lowercase variant search phrases users actually type. */
  variants: string[];
}

export const SEO_ALIASES: Record<string, SeoAlias> = {
  "/bmi-calculator": {
    variants: ["bmi", "bmi chart", "bmi formula", "bmi calculator women", "body mass index", "bmi calculator for women", "bmi calculator for men"],
  },
  "/bmr-calculator": {
    variants: ["basal metabolic rate calculator", "metabolic rate calculator", "bmr formula"],
  },
  "/calorie-calculator": {
    variants: ["calorie calculator to lose weight", "weight loss calculator", "calorie deficit calculator", "daily calorie needs"],
  },
  "/body-fat-calculator": {
    variants: ["body fat percentage calculator", "body fat percent", "navy body fat calculator"],
  },
  "/height-calculator": {
    variants: ["height predictor", "child height calculator", "adult height predictor"],
  },
  "/bra-size-calculator": {
    variants: ["bra size", "bra size finder", "correct bra size"],
  },

  "/loan-calculator": {
    variants: ["bank loan calculator", "personal loan calculator", "loan payment calculator", "loan repayment calculator"],
  },
  "/emi-calculator": {
    variants: ["emi calculator online", "monthly emi calculator", "loan emi calculator"],
  },
  "/interest-calculator": {
    variants: ["simple interest calculator", "interest rate calculator", "si calculator"],
  },
  "/compound-interest-calculator": {
    variants: ["compounding calculator", "daily compound interest calculator", "cumulative interest calculator", "compound interest formula", "compound interest"],
  },
  "/discount-calculator": {
    variants: ["discount price calculator", "sale price calculator", "percent off calculator"],
  },
  "/percentage-calculator": {
    variants: ["percent calculator", "percentage calculator online", "what percent of", "percentage change calculator"],
  },
  "/tax-calculator": {
    variants: ["income tax calculator", "tax return calculator", "bd income tax calculator", "tax estimator"],
  },
  "/vat-calculator": {
    variants: ["vat calculator online", "add vat calculator", "remove vat calculator"],
  },
  "/duty-calculator": {
    variants: ["customs duty calculator", "import duty calculator", "bd customs duty"],
  },

  "/age-calculator": {
    variants: ["birth calculator", "birth date calculator", "date of birth calculator", "age calculator by date of birth", "age counter", "calculate my age", "age calculator online", "how old am i"],
  },
  "/date-calculator": {
    variants: ["date to date calculator", "add days to date", "subtract days from date"],
  },
  "/days-calculator": {
    variants: ["day counter", "day to day calculator", "date counter", "time duration calculator", "days between dates"],
  },
  "/year-calculator": {
    variants: ["years between dates", "year difference calculator"],
  },
  "/month-calculator": {
    variants: ["months between dates", "month difference calculator"],
  },
  "/time-calculator": {
    variants: ["time addition calculator", "hours minutes calculator", "add time calculator"],
  },
  "/hours-calculator": {
    variants: ["work hours calculator", "time card calculator", "hours between times"],
  },
  "/hijri-date-converter": {
    variants: ["hijri to gregorian", "gregorian to hijri", "islamic date converter", "arabic date converter"],
  },

  "/scientific-calculator": {
    variants: ["scientific calculator online", "google calculator", "online calculator", "calculator online", "calculator app", "free scientific calculator"],
  },
  "/graphing-calculator": {
    variants: ["desmos graphing calculator", "desmos graphing", "online graphing calculator", "free graphing calculator"],
  },
  "/casio-calculator": {
    variants: ["casio scientific calculator", "casio calculator online"],
  },
  "/casio-fx-991ex": {
    variants: ["fx 991ex", "casio fx-991ex classwiz", "fx-991ex emulator"],
  },
  "/casio-fx-991es": {
    variants: ["casio fx 991es plus", "fx 991es plus", "fx-991es emulator"],
  },
  "/integral-calculator": {
    variants: ["integration calculator", "definite integral calculator", "indefinite integral calculator"],
  },
  "/math-solver": {
    variants: ["maths solver", "algebra calculator", "math problem solver", "equation solver"],
  },
  "/quadratic-formula-solver": {
    variants: ["quadratic formula", "quadratic equation solver", "quadratic equation calculator"],
  },
  "/binary-calculator": {
    variants: ["binary to decimal converter", "binary number calculator"],
  },
  "/gpa-calculator": {
    variants: ["gpa", "gpa calculator online", "cgpa calculator", "ssc gpa calculator", "hsc gpa calculator"],
  },

  "/cm-to-feet": {
    variants: ["cms to feet", "cm to feet converter", "centimeter to feet", "cm to ft"],
  },
  "/feet-to-meter": {
    variants: ["feet to meter converter", "feet to meters", "ft to m"],
  },
  "/kg-to-pounds": {
    variants: ["kg to pounds converter", "pound to kg converter", "kg to lbs", "lbs to kg"],
  },
  "/aspect-ratio-calculator": {
    variants: ["aspect ratio", "image aspect ratio", "video aspect ratio", "16:9 calculator"],
  },
  "/square-feet-calculator": {
    variants: ["square footage", "sqft calculator", "area calculator"],
  },
  "/distance-calculator": {
    variants: ["distance between cities", "distance between two points", "bd district distance"],
  },
  "/unit-converter": {
    variants: ["unit conversion", "metric converter", "online unit converter"],
  },
  "/land-calculator": {
    variants: ["katha to bigha", "bigha to decimal", "shotok to katha", "bd land converter"],
  },
  "/gbp-to-usd": {
    variants: ["english pounds to dollars", "pound to dollar", "gbp usd rate"],
  },

  "/zakat-calculator": {
    variants: ["zakat in bdt", "gold zakat calculator", "vori zakat", "islamic zakat calculator"],
  },
  "/prayer-times": {
    variants: ["namaz time", "salah time", "azan time bangladesh", "bd prayer schedule"],
  },
  "/sehri-iftar-time": {
    variants: ["iftar countdown", "ramadan calendar bd", "sehri time", "iftar time bangladesh"],
  },
  "/qibla-direction": {
    variants: ["qibla finder", "qibla compass", "kaaba direction", "qibla direction bangladesh"],
  },
  "/faraid-calculator": {
    variants: ["islamic inheritance calculator", "warish calculator", "mirath calculator", "faraiz calculator"],
  },
};

export function getAliases(path: string): string[] {
  return SEO_ALIASES[path]?.variants ?? [];
}
